import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'sapan_cart_v1';

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  // key unik per kombinasi produk + varian + catatan (mis. level es/gula beda dianggap baris terpisah)
  const keyOf = (productId, variantId, note) => `${productId}::${variantId || 'default'}::${note || ''}`;

  const addItem = useCallback((item, qty = 1) => {
    const key = keyOf(item.product_id, item.variant_id, item.note);
    setItems(prev => {
      // Keranjang cuma boleh berisi satu jenis pesanan: produk PO ATAU menu POS.
      // Kalau customer pindah mode (mis. PO baru dibuka setelah dia isi keranjang POS), kosongkan dulu.
      const cleaned = prev[0] && prev[0].kind !== item.kind ? [] : prev;
      const idx = cleaned.findIndex(i => i.key === key);
      if (idx >= 0) {
        const next = [...cleaned];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [...cleaned, { key, qty, ...item }];
    });
  }, []);

  const setQty = useCallback((key, qty) => {
    setItems(prev => {
      if (qty <= 0) return prev.filter(i => i.key !== key);
      return prev.map(i => (i.key === key ? { ...i, qty } : i));
    });
  }, []);

  const removeItem = useCallback((key) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalQty = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const totalPrice = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);

  const value = { items, addItem, setQty, removeItem, clearCart, totalQty, totalPrice, keyOf };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart harus dipakai di dalam <CartProvider>');
  return ctx;
}
